import React from 'react'
import { SEMANTIC_STYLES, SEMANTIC_ORDER, DOMAIN_HERO } from './colors'
import { SERIES_STYLES, styleBackground, type SeriesStyle } from './seriesStyles'

// Known categories pin to a fixed DOMAIN style (so REC keeps its blue, Solar its
// amber, a gas its blue - each with its texture - on every chart); everything
// else (locations, facilities) uses ONE primary Tellus blue - multi-shade location
// colouring read as busy, and bar length already carries the comparison.

// Part-to-whole categorical breakdowns (facility, technology, commodity type)
// rendered as lightweight CSS bars/strips instead of pie/donut slices. Length
// and aligned labels are far easier to compare than angles, single-category
// data degrades to one clean bar instead of a featureless circle, and slivers
// stay visible and labelled. It is a drop-in replacement for the former
// pie/donut chart: it reads the same { label, value, subLabel } items the
// stats/analytics services already produce, reusing the pre-formatted subLabel
// (e.g. "120MW", "1,200 tons of CO₂") the pie threw away. Colour is assigned
// here from the Tellus brand palette: known categories keep a pinned colour
// (statsColors), everything else gets a distinct palette colour by rank, so no
// two categories in a chart look alike.
export type CategoryBreakdownVariant = 'bars' | 'composition' | 'stat'

interface CategoryDatum {
  [key: string]: any
}

interface CategoryBreakdownChartProps {
  data: CategoryDatum[]
  title?: string
  variant?: CategoryBreakdownVariant
  xAxisDataKey?: string
  dataKey?: string
  minHeight?: number
  totalLabel?: string
  valueFormatter?: (value: number) => string
  // Accepted for drop-in parity with the former pie chart; the breakdown is now
  // integral to every variant, so these no longer toggle a separate table.
  showLegend?: boolean
  showValueLabels?: boolean
  otherThreshold?: number
  itemsPerPage?: number
}

interface Row {
  name: string
  value: number
  subLabel?: string
  note?: string
  style: SeriesStyle
}

const formatPercent = (pct: number): string => {
  if (!isFinite(pct) || pct <= 0) return '0%'
  if (pct >= 99.95) return '100%'
  return `${Math.round(pct * 10) / 10}%`
}

// "120MW" -> "MW", "3 projects" -> "projects", "1,200 tons of CO₂" -> "tons of CO₂"
const stripLeadingNumber = (text: string): string => text.replace(/^[\s\d.,]+/, '').trim()

const Card = ({ title, children }: { title?: string; children: React.ReactNode }) => (
  <div className="bg-white rounded-lg border border-gray-200 shadow-xs p-4">
    {title && <div className="text-lg font-semibold text-gray-800 mb-4">{title}</div>}
    {children}
  </div>
)

const CategoryBreakdownChart = ({
  data,
  title = '',
  variant = 'bars',
  xAxisDataKey = 'label',
  dataKey = 'value',
  minHeight = 300,
  totalLabel = 'Total',
  valueFormatter,
}: CategoryBreakdownChartProps) => {
  // Known domain categories keep their canonical order (REC, SREC, VER; Solar,
  // Wind, Geothermal) on every chart so the sequence never shuffles by value.
  // Unknown categories (facilities, locations) rank by value, largest first.
  const orderOf = (name: string): number | undefined => SEMANTIC_ORDER[name.toLowerCase()]
  const sorted = (data || [])
    .map((item) => ({
      name: String(item[xAxisDataKey] ?? item.label ?? ''),
      value: Number(item[dataKey] ?? item.value) || 0,
      subLabel: typeof item.subLabel === 'string' ? item.subLabel : undefined,
      note: typeof item.note === 'string' ? item.note : undefined,
    }))
    .sort((a, b) => {
      const oa = orderOf(a.name)
      const ob = orderOf(b.name)
      if (oa !== undefined && ob !== undefined) return oa - ob
      if (oa !== undefined) return -1
      if (ob !== undefined) return 1
      return b.value - a.value
    })

  // Style each row. BOUNDED domains (reductions, green-power, gases, deal stages)
  // keep their pinned domain shade - REC blue, Solar amber, a gas blue - the same on
  // every chart. UNBOUNDED members (locations, facilities) all share ONE primary
  // Tellus blue: per-location shades read as busy, and the row label + bar length
  // already tell them apart.
  const rows: Row[] = sorted.map((row) => ({
    ...row,
    style: SEMANTIC_STYLES[row.name.toLowerCase()] ?? SERIES_STYLES[0],
  }))

  if (rows.length === 0) {
    return (
      <Card title={title}>
        <div className="flex items-center justify-center text-gray-500" style={{ height: minHeight }}>
          No data available
        </div>
      </Card>
    )
  }

  const total = rows.reduce((sum, row) => sum + row.value, 0)
  const pct = (value: number) => (total ? (value / total) * 100 : 0)

  // The stat & bars cards use ONE colour - the card's DOMAIN hue - rather than a
  // shade per member: multi-colour dashboards read as busy, and the row label + bar
  // length already carry the comparison. Resolve the hue from the domain (every
  // reductions card is the same brand blue, every green-power card the hero amber),
  // NOT the first member's shade - otherwise a card with only SREC/VER would render a
  // different blue than a card that includes REC. (composition / mixed-unit / legend
  // keep per-member colour so stacked segments stay distinguishable.)
  const cardStyle: SeriesStyle =
    DOMAIN_HERO[rows[0]?.name.toLowerCase() ?? ''] ?? rows[0]?.style ?? SERIES_STYLES[0]

  const valueText = (row: Row): string =>
    row.subLabel ?? (valueFormatter ? valueFormatter(row.value) : row.value.toLocaleString())

  // Optional secondary figure (e.g. the native "730 MWh" behind a converted
  // tCO₂e value), shown muted after the primary value in the breakdown.
  const noteSuffix = (row: Row): string => (row.note ? ` (${row.note})` : '')

  const units = rows.map((row) => (row.subLabel ? stripLeadingNumber(row.subLabel) : '')).filter(Boolean)
  // Compare units case- and plural-insensitively so "1 project" / "2 projects"
  // (or "1 listing" / "2 listings") count as the same unit, while genuinely
  // different units ("tons" vs "MW") stay distinct.
  const normalizeUnit = (unit: string) => unit.toLowerCase().replace(/s$/, '')
  const distinctUnits = Array.from(new Set(units.map(normalizeUnit)))
  const uniformUnit = units.length === rows.length && distinctUnits.length === 1 ? units[0] : ''
  const totalText = valueFormatter
    ? valueFormatter(total)
    : `${Math.round(total).toLocaleString()}${uniformUnit ? ` ${uniformUnit}` : ''}`

  // Categories with different units (e.g. VER in tons CO₂ vs SREC in MWh) can't
  // be summed or compared as shares, so we never fabricate a total, a
  // percentage or a proportional bar across them: just list each value with its
  // own unit.
  if (distinctUnits.length > 1) {
    return (
      <Card title={title}>
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.name} className="flex items-center gap-3 text-sm">
              <span className="h-3.5 w-3.5 shrink-0 rounded-[3px]" style={styleBackground(row.style)} />
              <span className="flex-1 truncate text-gray-700" title={row.name}>{row.name}</span>
              <span className="tabular-nums font-medium text-gray-800">{valueText(row)}{noteSuffix(row)}</span>
            </div>
          ))}
        </div>
      </Card>
    )
  }

  const legend = (
    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
      {rows.map((row) => (
        <span key={row.name} className="inline-flex items-center gap-2">
          <span className="h-3.5 w-3.5 shrink-0 rounded-[3px]" style={styleBackground(row.style)} />
          <span className="font-medium text-gray-800">{row.name}</span>
          <span className="tabular-nums text-gray-400">{valueText(row)}{noteSuffix(row)} · {formatPercent(pct(row.value))}</span>
        </span>
      ))}
    </div>
  )

  if (variant === 'composition') {
    return (
      <Card title={title}>
        <div className="flex h-8 w-full overflow-hidden rounded-lg" role="img" aria-label={`${title} composition`}>
          {rows.map((row) => {
            const share = pct(row.value)
            return (
              <div
                key={row.name}
                className="flex h-full items-center justify-center overflow-hidden"
                style={{ width: `${share}%`, ...styleBackground(row.style) }}
                title={`${row.name}: ${valueText(row)} (${formatPercent(share)})`}
              >
                {share >= 9 && (
                  <span className="truncate px-1 text-xs font-semibold text-white">{formatPercent(share)}</span>
                )}
              </div>
            )
          })}
        </div>
        {legend}
      </Card>
    )
  }

  if (variant === 'stat') {
    return (
      <Card>
        <div className="mb-4 flex items-baseline justify-between gap-4">
          {title && <div className="text-[13px] font-semibold leading-snug text-gray-500">{title}</div>}
          <div className="whitespace-nowrap text-3xl font-bold tracking-tight tabular-nums text-gray-900">{totalText}</div>
        </div>
        <div className="space-y-2.5">
          {rows.map((row) => {
            const share = pct(row.value)
            return (
              <div key={row.name} className="flex items-center gap-3 text-sm">
                <span className="w-20 shrink-0 truncate text-gray-700" title={row.name}>{row.name}</span>
                <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100">
                  <div className="h-full rounded-full" style={{ width: `${share}%`, ...styleBackground(cardStyle) }} />
                </div>
                <span className="shrink-0 whitespace-nowrap text-right tabular-nums text-gray-600">
                  {valueText(row)}{noteSuffix(row)} <span className="ml-1 text-xs text-gray-400">{formatPercent(share)}</span>
                </span>
              </div>
            )
          })}
        </div>
      </Card>
    )
  }

  // variant === 'bars': sorted horizontal bars, each in a light rounded track,
  // with the value + % aligned in a fixed right-hand column.
  return (
    <Card title={title}>
      <div className="space-y-3">
        {rows.map((row) => {
          const share = pct(row.value)
          return (
            <div key={row.name} className="grid grid-cols-[13rem_1fr_10rem] items-center gap-3">
              <div className="truncate text-sm text-gray-700" title={row.name}>{row.name}</div>
              <div className="h-2.5 overflow-hidden rounded-full bg-gray-100">
                <div className="h-full rounded-full" style={{ width: `${share}%`, ...styleBackground(cardStyle) }} />
              </div>
              <div className="whitespace-nowrap text-right text-sm tabular-nums text-gray-800">
                {valueText(row)} <span className="ml-1 text-xs text-gray-400">{formatPercent(share)}</span>
              </div>
            </div>
          )
        })}
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-3 text-sm">
        <span className="font-semibold text-gray-800">{totalLabel}</span>
        <span className="font-semibold tabular-nums text-gray-800">{totalText}</span>
      </div>
    </Card>
  )
}

export default CategoryBreakdownChart
